/**
 * MVP-490: `/` reaches the Library search field from every console view, not just Library.
 *
 * `LibraryScreen` has handled `/` since AC-UI-5, but its listener is gated on `active` — so from
 * the other six views (`cockpit`, `settings`, `ingest`, `users`, `activity`, `withheld`) the key
 * was simply dead. This module is the console-wide half: `App.tsx` binds ONE `document` keydown
 * that switches to Library and asks it to focus its field. The two halves are mutually exclusive by
 * `view` — `shouldOpenConsoleSearch` answers false on `library`, where the screen's own listener
 * owns the key — so no keystroke is ever handled twice.
 *
 * Deliberately NOT routed through `useHotkeys`/`WIRED_COCKPIT_ACTIONS`: that map is gated on the
 * Cockpit being the active view, which would fix one of the six dead views and leave five needing a
 * second mechanism anyway. It also means `controlsLocked` cannot apply to `/` by construction —
 * jumping to a search field changes nothing about playback.
 *
 * The binding is read from the effective (merged) hotkey table rather than hard-coded, so an
 * operator who remaps `search` in Settings > Shortcuts gets that key console-wide for free — on
 * these six views. Library is the exception: `libraryActions.ts` still hard-codes `/`, so after a
 * remap to (say) `f`, `f` reaches search from the six and `/` still does on Library. Unifying that
 * is not MVP-490's to do.
 */

import { isTypingTarget, matchesBinding, type HotkeyAction } from './hotkeys.ts';

interface MinimalKeyboardEvent {
  key: string;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  metaKey: boolean;
}

interface ConsoleSearchContext {
  /** The console view currently showing — `library` means this key is not ours. */
  view: string;
  /** `isTypingTarget(event.target)` from hotkeys.ts — a `/` typed into a field is a slash. */
  isTypingTarget: boolean;
  /** Live `[role="dialog"]` check: a jump to another screen would pull the rug from under it. */
  isDialogOpen: boolean;
  /**
   * SettingsScreen.tsx is arming a `window` keydown to record the operator's next key as a new
   * binding. A `document`-level bubble handler fires BEFORE a `window` one, so without this guard
   * pressing `/` mid-capture would BOTH switch to Library and record `/` — on a screen that just
   * went away. `App.tsx` answers it from the `[data-hotkey-capture]` element that screen renders
   * exactly while capturing, the same live-DOM idiom as the dialog check above.
   */
  isCapturingHotkey: boolean;
  /**
   * `AdminIngestScreen` is rendering its `[data-ingest-running]` marker: a batch upload is live and
   * that screen is conditionally rendered, so switching away unmounts it, drops the picked `File`
   * objects and aborts the run. One keystroke must not do more than the Stop button does.
   */
  isIngestRunning: boolean;
}

export function shouldOpenConsoleSearch(
  ev: MinimalKeyboardEvent,
  ctx: ConsoleSearchContext,
  bindings: Record<HotkeyAction, string>
): boolean {
  if (ctx.view === 'library') return false;
  if (ctx.isTypingTarget || ctx.isDialogOpen || ctx.isCapturingHotkey || ctx.isIngestRunning) return false;
  return matchesBinding(ev, bindings.search);
}

/**
 * The cross-boundary handoff. `LibraryScreen` is lazy (`App.tsx`'s `Suspense`), so on the very
 * first `/` it has not mounted yet and there is nothing for `App` to focus in that commit — this is
 * the same problem `pendingAutoplayRef.ts` solves for playback, and takes the same shape: a module
 * singleton one side writes and the other consumes. No waiting mechanism is needed because the
 * consumer IS the component that mounts.
 *
 * Failure mode is benign by design. If the lazy chunk never arrives, this flag is a boolean nobody
 * else reads and the view still switched: `/` degrades to "switch to Library", never to a crash of
 * its own making. (A rejected `import()` taking the console tree down is a pre-existing app-wide
 * gap — `App.tsx` has no error boundary around either `Suspense` — and is not MVP-490's to fix.)
 */
const pendingSearchFocus = { current: false };

export function requestSearchFocus(): void {
  pendingSearchFocus.current = true;
}

/** True at most once per `requestSearchFocus()` — one `/` must not steal focus again on some
 * unrelated later switch back to Library. */
export function consumeSearchFocus(): boolean {
  if (!pendingSearchFocus.current) return false;
  pendingSearchFocus.current = false;
  return true;
}

/**
 * The other way a request must end. If `/` is pressed before the lazy Library chunk resolves and
 * the operator clicks another view before the screen mounts, nothing ever consumes the flag — it is
 * a module singleton, so it then lives for the tab, and the next unrelated arrival at Library steals
 * focus into the search field. `LibraryScreen` calls this whenever `active` goes false, which is
 * both that case and the ordinary "left Library" one; the request is only ever honoured by the
 * commit the operator actually asked for.
 */
export function clearSearchFocus(): void {
  pendingSearchFocus.current = false;
}

// `isTypingTarget` is re-exported so the App-side caller gets the predicate and the decision from
// one import — there is exactly one right answer to "is the operator typing", and it lives in
// hotkeys.ts.
export { isTypingTarget };
